import { ApolloProvider } from '@apollo/client'
import { parse } from 'cookie'
import Head from 'next/head'
import Router from 'next/router'
import NProgress from 'nprogress'
import { Fragment, useEffect, useState } from 'react'
import '~lib/dayjs'

import { useInitApollo } from '~apollo/apollo-client'
import ErrorPage from '~common/ErrorPage'
import FeatureFlagsProvider from '~common/FeatureFlagsProvider'
import HistoryProvider from '~common/HistoryProvider'
import PortalRootsProvider from '~common/PortalRootsProvider'
import ThemeProvider from '~common/ThemeProvider'
import Gradient from '~icons/gradient.svg'
import { AUTH_DATA_KEYS, PortalRoots } from '~lib/constants'
import { isProduction } from '~lib/helpers'
import { isAuth, isGrowthToolPage } from '~lib/routes'
import '~styles/globals.scss'
import IntercomeChecklistScript from '~modules/scripts/containers/IntercomeChecklistScript'
import SegmentAnaliticsScript from '~modules/scripts/containers/SegmentAnaliticsScript'
import GlobalModals from '~pages/global-modals'
import Toast from '~shared/ui/Toast'
import { ExtendedNextAppProps } from '~types/api/next'
import 'react-toastify/dist/ReactToastify.css'
import 'nprogress/nprogress.css'

const MyApp = ({ Component, pageProps, router }: ExtendedNextAppProps) => {
  const apolloClient = useInitApollo(pageProps?.apolloState)

  const [domain, setDomain] = useState('')
  const [userId, setUserId] = useState(null)

  useEffect(() => {
    const cookie = parse(document.cookie)

    setUserId(cookie[AUTH_DATA_KEYS.USER_ID])
    setDomain(window.location.host)
  }, [router, router.pathname])

  useEffect(() => {
    document.body.classList?.remove('loading')
    Router.events.on('routeChangeStart', () => NProgress.start())
    Router.events.on('routeChangeComplete', () => NProgress.done())
    Router.events.on('routeChangeError', () => NProgress.done())
  }, [])

  useEffect(() => {
    /**
     * When pathname changes, check the "page" to decide
     * whether to display the checklist or not
     */
    if (window.Alvis) {
      window.Alvis('page')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Router?.router?.pathname])

  const Layout = Component.Layout || Fragment

  return (
    <ApolloProvider client={apolloClient}>
      <FeatureFlagsProvider domain={domain} userId={userId}>
        <ThemeProvider>
          <HistoryProvider>
            <Head>
              {isAuth(router.pathname) ? (
                <title>Ignition | Sign in</title>
              ) : (
                <>
                  <title>Ignition</title>
                  <meta name="robots" content="noindex" />
                </>
              )}

              <meta name="viewport" content="width=device-width, initial-scale=1" />
              {!isGrowthToolPage(router.pathname) && <IntercomeChecklistScript />}
              {isProduction() && !isGrowthToolPage(router.pathname) && <SegmentAnaliticsScript />}
            </Head>
            <Gradient />
            <PortalRootsProvider>
              {pageProps.notFound ? (
                <ErrorPage statusCode={404} />
              ) : (
                <Layout>
                  <Component {...pageProps} />
                  <Toast.Container />
                  <GlobalModals />
                </Layout>
              )}
            </PortalRootsProvider>

            {Object.values(PortalRoots).map((root) => (
              <div key={root} id={root} />
            ))}
          </HistoryProvider>
        </ThemeProvider>
      </FeatureFlagsProvider>
    </ApolloProvider>
  )
}

export default MyApp
